/* The "database" for the approval workflow, kept as JSON in the same shape as the SQL tables in db/schema.sql:
 *   { app_user: [ ...rows ], report_approval: [ ...rows ] }
 *
 * Where the JSON lives (first one that works wins):
 *   1. a JSON file on the server: db/srs-db.json via src/setupProxy.js in development, or the SAML server's data folder (server/src/jsonDb.js)
 *   2. this browser's localStorage                                                   (static builds)
 *   3. memory only                                                                   (storage blocked)
 *
 * To move to a real database, replace the four functions at the bottom with calls to an API over the same tables.
 */
import seed from '../demo/db/srs-db.seed.json';

const ENDPOINT = '/__srs_db';
const LS_KEY = 'srs_demo_db_v1';
const clone = (o) => JSON.parse(JSON.stringify(o));
const valid = (db) => !!db && Array.isArray(db.app_user) && Array.isArray(db.report_approval);

let kind = null;           // 'file' | 'browser' | 'memory'
let memory = null;

async function fromFile() {
  try {
    const res = await fetch(ENDPOINT);
    if (!res || !res.ok) return null;
    const type = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
    if (!type.includes('json')) return null;               // a static host answers unknown paths with index.html
    const db = await res.json();
    return valid(db) ? db : null;
  } catch { return null; }
}
function fromBrowser() {
  try {
    const text = window.localStorage.getItem(LS_KEY);
    if (!text) { const db = clone(seed); window.localStorage.setItem(LS_KEY, JSON.stringify(db)); return db; }
    const db = JSON.parse(text);
    return valid(db) ? db : null;
  } catch { return null; }
}

export async function loadDb() {
  const file = await fromFile();
  if (file) { kind = 'file'; return file; }
  const browser = fromBrowser();
  if (browser) { kind = 'browser'; return browser; }
  kind = 'memory';
  if (!memory) memory = clone(seed);
  return clone(memory);
}

export async function saveDb(db) {
  if (!valid(db)) throw new Error('Refusing to save an invalid database document.');
  if (kind === 'file') {
    const res = await fetch(ENDPOINT, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(db, null, 2) });
    if (!res.ok) {
      let why = ''; try { why = (await res.json()).detail || ''; } catch { /* not JSON */ }
      throw new Error(why || `Could not save to the JSON file (${res.status}).`);
    }
    return;
  }
  if (kind === 'browser') {
    try { window.localStorage.setItem(LS_KEY, JSON.stringify(db)); return; } catch { kind = 'memory'; }
  }
  memory = clone(db);
}

export const resetDb = () => saveDb(clone(seed));
export const storageLabel = () => (kind === 'file' ? 'a JSON file on the server' : kind === 'browser' ? 'this browser (localStorage)' : 'memory only (lost on reload)');
export const seedDb = () => clone(seed);
